/**
 * Arcade kart physics. Fun over realism: karts accelerate quickly, steer more
 * as they gain speed, drift when grip is low, slow down off-road, and can burn
 * a boost meter for a temporary top-speed bump. Everything stays on the y = 0
 * plane. State is mutated in place.
 *
 * Coordinate conventions (see src/types.ts):
 *   forward = (sin(heading), 0, cos(heading)); heading = 0 points toward +Z.
 */
import * as THREE from "three";
import { KartState, KartInput, VehicleStats, TrackData } from "./types";

// Reused scratch objects so stepping many karts allocates nothing per frame.
const _forward = new THREE.Vector3();
const _desired = new THREE.Vector3();
const _lateral = new THREE.Vector3();
const _forwardPart = new THREE.Vector3();
const _toPoint = new THREE.Vector3();
const _yawQuat = new THREE.Quaternion();
const _rollQuat = new THREE.Quaternion();
const _pitchQuat = new THREE.Quaternion();
const _tmpQuat = new THREE.Quaternion();
const _up = new THREE.Vector3(0, 1, 0);
const _right = new THREE.Vector3(1, 0, 0);

/** Clamp helper. */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Build a fresh kart at `position` facing `heading`. Position is forced onto the
 * y = 0 plane and the render orientation is a pure yaw about Y.
 */
export function createKart(position: THREE.Vector3, heading: number): KartState {
  const pos = new THREE.Vector3(position.x, 0, position.z);
  const bodyQuat = new THREE.Quaternion().setFromAxisAngle(_up, heading);
  return {
    position: pos,
    velocity: new THREE.Vector3(0, 0, 0),
    heading,
    speed: 0,
    boostMeter: 1,
    boosting: false,
    braking: false,
    slip: 0,
    offRoad: 0,
    bodyQuat,
  };
}

/**
 * Advance one kart by `dt` seconds under `input`. Mutates `state` in place and
 * keeps it grounded on y = 0.
 */
export function stepKart(
  state: KartState,
  input: KartInput,
  stats: VehicleStats,
  track: TrackData,
  dt: number,
): void {
  // Keep the sim stable even if a frame hitches.
  dt = clamp(dt, 0, 0.05);

  // -------------------------------------------------------------------------
  // Boost gating
  // -------------------------------------------------------------------------
  const boosting = input.boost && state.boostMeter > 0 && input.throttle >= 0;
  // Handbrake drift: only meaningful while actually moving.
  const drifting = input.drift && Math.abs(state.speed) > 6;

  // -------------------------------------------------------------------------
  // Longitudinal: move speed toward a throttle-derived target.
  //
  // Pushing against the direction of travel means "brake", not "reverse":
  // speed is scrubbed hard toward zero first, and only once the kart has
  // actually stopped does the same key start driving it backwards. That is how
  // a real pedal behaves and it stops S/Down from feeling like a wall.
  // -------------------------------------------------------------------------
  const topSpeed = stats.maxSpeed * (boosting ? stats.boostMul : 1);
  const throttle = input.throttle;
  const braking =
    (throttle < 0 && state.speed > 0.5) || (throttle > 0 && state.speed < -0.5);

  let target: number;
  if (braking) {
    target = 0;
  } else if (throttle > 0) {
    target = topSpeed * throttle;
  } else if (throttle < 0) {
    target = -stats.reverseSpeed * -throttle;
  } else {
    target = 0;
  }

  // Rate at which speed chases the target. Accelerate briskly (with a boost
  // bump); coast down harder than we accelerate so releasing throttle feels
  // snappy; brake harder still.
  const accel = stats.accel * (boosting ? stats.boostMul : 1);
  const coastDecel = stats.accel * 1.8;
  const brakeDecel = stats.accel * 3.4;

  const closingUp =
    Math.abs(target) > Math.abs(state.speed) && Math.sign(target) === Math.sign(state.speed || target);
  const rate = braking ? brakeDecel : closingUp ? accel : coastDecel;

  // Step speed toward target without overshooting.
  const diff = target - state.speed;
  const maxStep = rate * dt;
  if (Math.abs(diff) <= maxStep) {
    state.speed = target;
  } else {
    state.speed += Math.sign(diff) * maxStep;
  }

  // Coasting drag — applied only OFF the throttle, so holding the throttle
  // actually reaches the vehicle's top speed (maxSpeed stays meaningful).
  if (throttle === 0) {
    state.speed -= state.speed * 0.9 * dt;
  }

  // -------------------------------------------------------------------------
  // Steering: turn more as speed builds, ease off at the very top end, invert
  // when reversing.
  // -------------------------------------------------------------------------
  const absSpeed = Math.abs(state.speed);
  const speedFactor =
    clamp(absSpeed / 8, 0, 1) * (1 - 0.25 * clamp(absSpeed / stats.maxSpeed, 0, 1));
  // Drifting sharpens the turn-in so you can rotate the kart through the slide.
  const turnRate = stats.handling * input.steer * (drifting ? 1.35 : 1);
  const dirSign = state.speed < 0 ? -1 : 1; // reversing flips steering
  state.heading += turnRate * speedFactor * dirSign * dt;

  // -------------------------------------------------------------------------
  // Grip / drift. Blend the current velocity toward the desired heading-aligned
  // velocity. High grip kills lateral velocity fast; low grip keeps the slide.
  // -------------------------------------------------------------------------
  _forward.set(Math.sin(state.heading), 0, Math.cos(state.heading));
  _desired.copy(_forward).multiplyScalar(state.speed);

  // Decompose current velocity into forward + lateral components.
  const fwdSpeedComponent = state.velocity.dot(_forward);
  _forwardPart.copy(_forward).multiplyScalar(fwdSpeedComponent);
  _lateral.copy(state.velocity).sub(_forwardPart);

  // Fraction of lateral velocity retained this frame. grip=1 -> ~none retained;
  // lower grip -> more slide preserved. Framerate-independent decay.
  // Drifting slashes grip so lateral velocity is retained → the kart slides out.
  const gripStrength = (4 + stats.grip * 16) * (drifting ? 0.22 : 1);
  const lateralRetain = Math.exp(-gripStrength * dt);
  _lateral.multiplyScalar(lateralRetain);

  // Forward part snaps to the commanded speed; recombine.
  _forwardPart.copy(_forward).multiplyScalar(state.speed);
  state.velocity.copy(_forwardPart).add(_lateral);

  // Integrate position.
  state.position.addScaledVector(state.velocity, dt);

  // -------------------------------------------------------------------------
  // Off-road handling: sample the track at the new position.
  // -------------------------------------------------------------------------
  const sample = track.sample(state.position);
  // 0 on tarmac, ramping to 1 a few metres into the dirt. Drives dust, the
  // rumble in the audio mix and the HUD's off-road tint.
  const half = track.roadWidth / 2;
  state.offRoad = clamp((sample.distance - half) / 6, 0, 1);
  if (!sample.onRoad) {
    // Cap speed and bleed off extra energy in the dirt.
    const cap = stats.maxSpeed * 0.45;
    if (state.speed > cap) state.speed = cap;
    else if (state.speed < -cap) state.speed = -cap;

    // Extra drag on both the scalar speed and the world velocity vector.
    const offDrag = 1.5 * dt;
    state.speed -= state.speed * offDrag;
    state.velocity.multiplyScalar(Math.max(0, 1 - offDrag));
  }

  // -------------------------------------------------------------------------
  // Guardrail. The circuit is walled at `wallRadius`, so running wide costs you
  // a scrape along the armco rather than a free trip through the scenery: the
  // kart is pinned to the barrier, the component of velocity heading into it is
  // cancelled, and it keeps whatever speed it had running parallel.
  // -------------------------------------------------------------------------
  _toPoint.copy(state.position).sub(sample.point);
  _toPoint.y = 0;
  const fromCenter = _toPoint.length();
  if (fromCenter > track.wallRadius && fromCenter > 1e-4) {
    _toPoint.multiplyScalar(1 / fromCenter); // outward unit normal
    state.position.copy(sample.point).addScaledVector(_toPoint, track.wallRadius);
    const into = state.velocity.dot(_toPoint);
    if (into > 0) state.velocity.addScaledVector(_toPoint, -into * 1.15); // small rebound
    state.speed *= 0.7;
  }

  // -------------------------------------------------------------------------
  // Boost meter: drain while boosting (~2s of full boost), regen otherwise.
  // -------------------------------------------------------------------------
  if (boosting) {
    state.boostMeter = clamp(state.boostMeter - 0.5 * dt, 0, 1);
  } else {
    // Regenerate, faster while drifting — a little mini-turbo reward.
    const regen = drifting ? 0.45 : 0.2;
    state.boostMeter = clamp(state.boostMeter + regen * dt, 0, 1);
  }
  state.boosting = boosting;

  // -------------------------------------------------------------------------
  // Recompute the reported speed as the true forward-projected speed after the
  // grip blend, so the HUD shows what the kart is actually doing.
  // -------------------------------------------------------------------------
  _forward.set(Math.sin(state.heading), 0, Math.cos(state.heading));
  state.speed = state.velocity.dot(_forward);

  // -------------------------------------------------------------------------
  // Visual body orientation: yaw + a little bank (roll) into turns from lateral
  // slip and steering, plus slight pitch under accel/brake. Never fold lean
  // into heading.
  // -------------------------------------------------------------------------
  const lateralSpeed = state.velocity.dot(_right.set(_forward.z, 0, -_forward.x)); // right = forward rotated -90 about Y
  const bankFromSlip = clamp(-lateralSpeed / Math.max(stats.maxSpeed, 1), -1, 1);
  const bankFromSteer =
    -input.steer * clamp(Math.abs(state.speed) / stats.maxSpeed, 0, 1) * Math.sign(state.speed || 1);
  const roll = clamp((bankFromSlip * 0.25 + bankFromSteer * 0.3), -0.35, 0.35);

  // Pitch: nose up under acceleration, dip under braking.
  const accelSignal = braking ? -1 : throttle;
  const pitch = clamp(accelSignal * 0.06, -0.1, 0.1);

  // Publish the sideways scrub so the FX and audio layers can react to it.
  state.slip = Math.abs(lateralSpeed);
  state.braking = braking;

  _yawQuat.setFromAxisAngle(_up, state.heading);
  _rollQuat.setFromAxisAngle(new THREE.Vector3(0, 0, 1), roll);
  _pitchQuat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitch);
  // Compose: yaw first, then local pitch, then local roll.
  _tmpQuat.copy(_yawQuat).multiply(_pitchQuat).multiply(_rollQuat);
  state.bodyQuat.copy(_tmpQuat);

  // -------------------------------------------------------------------------
  // Stay grounded on the flat slice.
  // -------------------------------------------------------------------------
  state.position.y = 0;
  state.velocity.y = 0;
}
